import Search16 from '@carbon/icons-react/es/search/16';
import { List } from 'antd';
import { ListItemMetaProps } from 'antd/lib/list/Item';
import { initial } from 'lodash';
import React, { FC, useMemo } from 'react';
import { components, OptionTypeBase } from 'react-select';
import styled, { css } from 'styled-components';
import { Control, Select, SelectProps } from 'components/atoms/select';
import { SingleLineEllipsis } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { VirtualizedMenuList } from './virtualised-list';

const selectStyles = {
  menu: () => ({
    boxShadow: 'none',
    flex: 1,
    overflow: 'hidden',
    marginTop: 'var(--spacing-05) !important',
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    maxHeight: initial,
    height: '100%',
  }),
  control: (provided) => ({ ...provided, margin: '0 var(--spacing-05)' }),
  group: (provided) => ({ ...provided, paddingTop: 0, paddingBottom: 0 }),
  groupHeading: (provided) => ({ ...provided, marginBottom: 0, flex: 1 }),
  option: (provided) => ({
    ...provided,
    paddingLeft: 'var(--spacing-05)',
    paddingRight: 'var(--spacing-05)',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    display: 'flex !important',
  }),
};

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <Search16 />
  </components.DropdownIndicator>
);

const ListItem = styled(List.Item)`
  padding: 0;
`;

type MetaProps = ListItemMetaProps & {
  isFocused: boolean;
  isSelected: boolean;
};

const ListItemMeta = styled(List.Item.Meta)<MetaProps>`
  align-items: center;
  & .ant-list-item-meta-title {
    margin-bottom: 0;
    ${SingleLineEllipsis};
    ${({ isSelected }) =>
      isSelected
        ? css`
            color: var(--white-100) !important;
          `
        : css`
            color: var(--gray-100) !important;
          `};
  }
  & .ant-list-item-meta-description {
    font-size: var(--font-size-sm);
    ${({ isSelected }) =>
      isSelected
        ? css`
            color: var(--white-100);
          `
        : css`
            color: var(--text-02);
          `};
    font-weight: normal;
  }
`;

const CustomOption = (props): JSX.Element => {
  const { data, isSelected, isFocused } = props;

  return (
    <components.Option {...props}>
      <ListItem>
        <ListItemMeta
          isFocused={isFocused}
          isSelected={isSelected}
          title={data.label}
        />
      </ListItem>
    </components.Option>
  );
};

export const GroupedDropdownSelect: FC<SelectProps<OptionTypeBase, false>> = (
  props,
): JSX.Element => {
  const { components: customComponents, options, ...rest } = props;
  const isDefault = useMemo(
    () => (options?.length > 0 && options?.[0]?.id === 'default') ?? true,
    [options],
  );

  return (
    <Select
      autoFocus
      menuIsOpen
      isClearable
      backspaceRemovesValue
      components={{
        ClearIndicator: null,
        IndicatorSeparator: null,
        DropdownIndicator,
        Control,
        Option: CustomOption,
        MenuList: VirtualizedMenuList,
        ...customComponents,
      }}
      controlShouldRenderValue={false}
      hideSelectedOptions={false}
      placeholder={useTranslation('placeholder.search')}
      tabSelectsValue={false}
      styles={selectStyles}
      {...rest}
      options={isDefault ? options[0]?.options : options}
      grouped={!isDefault}
      type="basic"
    />
  );
};
